"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { Loader2 } from "lucide-react";
import { CrosshairIcon, MapPinIcon, ArrowLeftIcon, XIcon, XCircleIcon } from "@phosphor-icons/react";
import { BsSearch } from "react-icons/bs";
import { GoogleMap } from "@react-google-maps/api";
import { useI18N, useModalHeight } from "common/hooks";
import { getSuggestions, Suggestion } from "common/apis/search";
import { reverseGeocode as reverseGeocodeApi } from "common/apis";
import { darkModeStyles, lightModeStyles } from "common/config";
import { ModalHeader } from "@/components/common/ModalHeader";
import PopularCities from "../PopularCities/PopularCities";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useScrollLock } from "@/hooks/useScrollLock";
import YouAre from "@/assets/pins/youAre.svg";

// Helper function to sanitize input by removing HTML tags
const sanitizeInput = (text: string): string => {
  if (!text) return "";
  const withoutHtml = text.replace(/<[^>]*>/g, "");
  return withoutHtml.trim().replace(/\s+/g, "");
};

interface ChangeLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCurrentLocationPress: () => void;
  onLocationSelect: (text: string) => void;
  latitude: number;
  longitude: number;
}

export default function ChangeLocationModal({
  isOpen,
  onClose,
  onCurrentLocationPress,
  onLocationSelect,
  latitude,
  longitude,
}: ChangeLocationModalProps) {
  const i18n = useI18N();
  useScrollLock(isOpen);
  const { modalStyle, modalClassName } = useModalHeight();
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const defaultButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Map picker state
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [selectedCoords, setSelectedCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: latitude,
    lng: longitude,
  });
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const { isMapsLoaded } = useLoadGoogleMaps();
  const isDark = useDarkMode();

  // Debounced suggestions fetch
  const debouncedGetSuggestions = useCallback(
    (text: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (text.trim().length > 0) {
          setLoadingSuggestions(true);
          try {
            const list = await getSuggestions(text, {
              latitude,
              longitude,
              size: 5,
            });

            // Filter for location suggestions only
            const locationSuggestions = list.filter(
              (s) => s.suggestion_type === "location",
            );

            setSuggestions(locationSuggestions || []);

            // Track search performed
            AnalyticsService.trackGAEventHandler(
              "changeLocationSearchPerformed",
            );
            AnalyticsService.trackMx("changeLocationSearchPerformed", {
              SearchTerm: text,
              ResultsCount: locationSuggestions.length,
            });
          } catch (error) {
            console.error("Error fetching location suggestions:", error);
            setSuggestions([]);
          } finally {
            setLoadingSuggestions(false);
          }
        } else {
          setSuggestions([]);
          setLoadingSuggestions(false);
        }
      }, 300);
    },
    [latitude, longitude],
  );

  // Effect to trigger suggestions on search text change
  useEffect(() => {
    debouncedGetSuggestions(searchText);
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchText, debouncedGetSuggestions]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText("");
      setSuggestions([]);
      setHighlightIndex(-1);
      setShowMapPicker(false);
      setSelectedAddress(null);
      setSelectedCoords(null);
      setMapCenter({ lat: latitude, lng: longitude });
    }
  }, [isOpen, latitude, longitude]);

  // Scroll highlighted item into view
  useEffect(() => {
    const showSuggestions = searchText.trim().length > 0;
    if (highlightIndex >= 0 && showSuggestions) {
      const activeEl = document.getElementById(
        `location-suggest-item-${highlightIndex}`,
      );
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightIndex, searchText]);

  // Reset map center when showing map picker
  useEffect(() => {
    if (showMapPicker) {
      setMapCenter({ lat: latitude, lng: longitude });
    }
  }, [showMapPicker, latitude, longitude]);

  // Reverse geocoding function
  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      setIsLoadingAddress(true);
      try {
        const data = await reverseGeocodeApi(lat, lng);

        const results = data.data?.results;

        if (results && results.length > 0) {
          const address = results[0].formatted_address;
          // Use coordinates from the result if available, otherwise use the input coordinates
          const resultCoords = results[0].geometry?.location;
          setSelectedAddress(address);
          setSelectedCoords(
            resultCoords
              ? { lat: resultCoords.lat, lng: resultCoords.lng }
              : { lat, lng },
          );
        } else {
          setSelectedAddress(
            i18n.get("locationNotFound") || "Location not found",
          );
        }
      } catch (error) {
        console.error("Reverse geocoding error:", error);
        setSelectedAddress(
          i18n.get("errorFetchingLocation") || "Error fetching location",
        );
      } finally {
        setIsLoadingAddress(false);
      }
    },
    [i18n],
  );

  // Handle map drag end
  const handleMapDragEnd = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      if (center) {
        const newLat = center.lat();
        const newLng = center.lng();
        // Update the map center state to keep the map at the new position
        setMapCenter({ lat: newLat, lng: newLng });
        reverseGeocode(newLat, newLng);
      }
    }
  }, [mapRef, reverseGeocode]);

  // Handle map load
  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      setMapRef(map);
      // Initial reverse geocode for current center
      reverseGeocode(mapCenter.lat, mapCenter.lng);
    },
    [mapCenter.lat, mapCenter.lng, reverseGeocode],
  );

  // Handle set location on map button click
  const handleSetLocationOnMapPress = useCallback(() => {
    AnalyticsService.trackGAEventHandler("changeLocationSetOnMapClicked");
    AnalyticsService.trackMx("changeLocationSetOnMapClicked", {});
    setShowMapPicker(true);
  }, []);

  // Handle back from map picker
  const handleBackFromMapPicker = useCallback(() => {
    setShowMapPicker(false);
    setSelectedAddress(null);
    setSelectedCoords(null);
  }, []);

  // Handle clear selected location
  const handleClearLocation = useCallback(() => {
    setSelectedAddress(null);
    setSelectedCoords(null);
    // Reset map to original position
    setMapCenter({ lat: latitude, lng: longitude });
    if (mapRef) {
      mapRef.panTo({ lat: latitude, lng: longitude });
    }
    reverseGeocode(latitude, longitude);
  }, [mapRef, latitude, longitude, reverseGeocode]);

  // Handle confirm location selection
  const handleConfirmLocation = useCallback(() => {
    if (selectedAddress && selectedCoords) {
      AnalyticsService.trackGAEventHandler(
        "changeLocationMapLocationConfirmed",
      );
      AnalyticsService.trackMx("changeLocationMapLocationConfirmed", {
        Address: selectedAddress,
        Latitude: selectedCoords.lat,
        Longitude: selectedCoords.lng,
      });
      onLocationSelect(selectedAddress);
      onClose();
    }
  }, [selectedAddress, selectedCoords, onLocationSelect, onClose]);

  const handleClose = useCallback(() => {
    AnalyticsService.trackGAEventHandler("changeLocationModalClosed");
    AnalyticsService.trackMx("changeLocationModalClosed", {});
    onClose();
  }, [onClose]);

  const handleCurrentLocationPress = useCallback(() => {
    AnalyticsService.trackGAEventHandler(
      "changeLocationCurrentLocationClicked",
    );
    AnalyticsService.trackMx("changeLocationCurrentLocationClicked", {});
    onCurrentLocationPress();
    onClose();
  }, [onCurrentLocationPress, onClose]);

  const handleSuggestionPress = useCallback(
    (suggestion: Suggestion) => {
      AnalyticsService.trackGAEventHandler("changeLocationSuggestionSelected");
      AnalyticsService.trackMx("changeLocationSuggestionSelected", {
        SuggestionText: suggestion.text,
        SuggestionType: suggestion.suggestion_type,
      });
      onLocationSelect(suggestion.text);
      onClose();
    },
    [onLocationSelect, onClose],
  );

  const handleSearchTextPress = useCallback(() => {
    const trimmedText = searchText.trim();
    if (trimmedText) {
      AnalyticsService.trackGAEventHandler("changeLocationSearchTextSelected");
      AnalyticsService.trackMx("changeLocationSearchTextSelected", {
        SearchText: trimmedText,
      });
      onLocationSelect(trimmedText);
      onClose();
    }
  }, [searchText, onLocationSelect, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const showSuggestions = searchText.trim().length > 0;
      
      // Handle default view navigation (Current Location, Map, Popular Cities)
      if (!showSuggestions) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          // Focus first default button (Current Location)
          defaultButtonRefs.current[0]?.focus();
          return;
        }
        return;
      }

      const displayItems =
        searchText.trim().length > 0
          ? [
              { kind: "query" as const },
              ...suggestions.map((s) => ({
                kind: "suggestion" as const,
                data: s,
              })),
            ]
          : [];

      if (displayItems.length === 0) return;

      if (e.key === "ArrowDown") {
        setHighlightIndex((prev) =>
          prev < 0 ? 0 : (prev + 1) % displayItems.length,
        );
        e.preventDefault();
      } else if (e.key === "ArrowUp") {
        setHighlightIndex((prev) =>
          prev <= 0 ? displayItems.length - 1 : prev - 1,
        );
        e.preventDefault();
      } else if (e.key === "Enter") {
        const indexToUse = highlightIndex >= 0 ? highlightIndex : 0;
        const item = displayItems[indexToUse];
        if (item) {
          if (item.kind === "query") {
            handleSearchTextPress();
          } else {
            handleSuggestionPress(item.data);
          }
        }
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === "Escape") {
        setSearchText("");
        setSuggestions([]);
        setHighlightIndex(-1);
        e.preventDefault();
      }
    },
    [
      searchText,
      suggestions,
      highlightIndex,
      handleSearchTextPress,
      handleSuggestionPress,
    ],
  );

  // Handle keyboard navigation for default buttons (Current Location, Map)
  const handleDefaultButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = index + 1;
        if (nextIndex < defaultButtonRefs.current.length) {
          defaultButtonRefs.current[nextIndex]?.focus();
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (index === 0) {
          // Go back to search input
          inputRef.current?.focus();
        } else {
          const prevIndex = index - 1;
          defaultButtonRefs.current[prevIndex]?.focus();
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        defaultButtonRefs.current[defaultButtonRefs.current.length - 1]?.focus();
      }
    },
    [],
  );

  const handleCityPress = useCallback(
    (cityName: string, countryName: string, count: number) => {
      AnalyticsService.trackGAEventHandler("changeLocationCitySelected");
      AnalyticsService.trackMx("changeLocationCitySelected", {
        CityName: cityName,
        CountryName: countryName,
        VenueCount: count,
      });
      onLocationSelect(`${cityName} ${countryName}`);
      onClose();
    },
    [onLocationSelect, onClose],
  );

  // Determine whether to show the suggestions view or the default view
  const showSuggestionsView = searchText.trim().length > 0;

  const renderSuggestions = () => {
    if (loadingSuggestions) {
      return (
        <div className="py-4 flex items-center justify-center">
          <Loader2 className="animate-spin text-fg-brand-subtle" size={24} />
        </div>
      );
    }

    const trimmedText = searchText.trim();
    const hasText = trimmedText.length > 0;

    if (!hasText) {
      return null;
    }

    return (
      <div className="divide-y divide-primary-100" role="listbox" id="location-suggestions-listbox">
        {/* Press Enter to Search Option */}
        <button
          ref={(el) => {
            if (el) suggestionRefs.current[0] = el;
          }}
          type="button"
          onClick={handleSearchTextPress}
          id="location-suggest-item-0"
          role="option"
          aria-selected={highlightIndex === 0}
          tabIndex={-1}
          className={cn(
            "w-full py-3 flex items-center gap-3 transition-colors text-left rounded-lg px-2 -mx-2",
            highlightIndex === 0
              ? "bg-bg-brand-tertiary-active "
              : "hover:bg-bg-brand-secondary-hover",
          )}
        >
          <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 min-w-[24px]">
            <BsSearch className="text-fg-brand-subtle" size={16} />
          </div>
          <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span className="body-l-semibold text-fg-neutral-strong truncate max-w-full">
              {trimmedText}
            </span>
            <span className="body-s-regular text-fg-neutral-subtle truncate max-w-full">
              {i18n.get("pressEnterToSearch")}
            </span>
          </div>
        </button>

        {/* Location Suggestions */}
        {suggestions.length > 0 ? (
          suggestions.map((item, index) => {
            const displayIndex = index + 1; // +1 because the"press enter" option is at index 0
            return (
              <button
                key={item.id || index}
                ref={(el) => {
                  if (el) suggestionRefs.current[displayIndex] = el;
                }}
                type="button"
                onClick={() => handleSuggestionPress(item)}
                id={`location-suggest-item-${displayIndex}`}
                role="option"
                aria-selected={highlightIndex === displayIndex}
                tabIndex={-1}
                className={cn(
                  "w-full py-3 flex items-center gap-3 transition-colors text-left rounded-lg px-2 -mx-2",
                  highlightIndex === displayIndex
                    ? "bg-bg-brand-tertiary-active "
                    : "hover:bg-bg-brand-secondary-hover",
                )}
              >
                <MapPinIcon
                  size={24}
                  className="text-fg-neutral-strong flex-shrink-0"
                />
                <span className="label-m-medium text-fg-neutral-strong line-clamp-2">
                  {sanitizeInput(item.text)}
                </span>
              </button>
            );
          })
        ) : (
          <div className="py-4 text-center">
            <span className="label-m-medium text-fg-neutral-subtle">
              {i18n.get("noSuggestionsFound")}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      focusTrapped={true}
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={handleClose}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[95vw] md:w-[520px]",
        closeButton: "hidden",
      }}
    >
      <section
        style={modalStyle}
        className={cn(modalClassName, "w-full md:w-[520px] overflow-hidden")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={showMapPicker ? "location-modal-map-title" : "location-modal-title"}
      >
        {/* Modal Header */}
        {showMapPicker ? (
          // Custom header with back button for map picker
          <header className="py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal sticky top-0 bg-bg-brand-secondary-default rounded-t-2xl">
            <button
              type="button"
              onClick={() => {
                AnalyticsService.trackGAEventHandler("changeLocationBackClicked");
                AnalyticsService.trackMx("changeLocationBackClicked", {});
                handleBackFromMapPicker();
              }}
              className="p-2 hover:bg-bg-brand-ghost-hover rounded-lg transition-colors flex-shrink-0"
              aria-label={i18n.get("back")}
            >
              <ArrowLeftIcon size={20} className="text-fg-brand-subtle" />
            </button>
            <h1 id="location-modal-map-title" className="text-center text-fg-neutral-strong flex-1 px-4 title-s-semibold">
              {i18n.get("selectLocation")}
            </h1>
            <div className="flex-shrink-0 w-10"></div>
          </header>
        ) : (
          <ModalHeader
            title={i18n.get("selectLocation")}
            onClose={handleClose}
            titleId="location-modal-title"
          />
        )}

        <div className="flex flex-col flex-1 w-full bg-bg-brand-secondary-default rounded-b-2xl overflow-hidden">
          {showMapPicker ? (
            // Map Picker View
            <div className="flex flex-col h-[500px]">
              {/* Map Container */}
              <div className="relative flex-1">
                {isMapsLoaded ? (
                  <>
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={16}
                      onLoad={handleMapLoad}
                      onDragEnd={handleMapDragEnd}
                      options={{
                        disableDefaultUI: true,
                        zoomControl: true,
                        styles: isDark ? darkModeStyles : lightModeStyles,
                      }}
                    />
                    {/* Static Center Marker */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-10">
                      <img
                        src={YouAre}
                        alt="Location marker"
                        className="w-6 h-6"
                      />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-container-neutral-minimal">
                    <Loader2
                      className="animate-spin text-fg-brand-subtle"
                      size={32}
                    />
                  </div>
                )}
              </div>

              {/* Location InfoIcon Panel */}
              <div className="p-4 border-t border-primary-100">
                <p className="body-s-regular text-fg-neutral-subtle mb-2">
                  {i18n.get("dragThePinOnTheMapToGetTheGeolocation") ||
                    "Drag the pin on the map to get the geolocation"}
                </p>

                {/* Selected Location Display */}
                <div className="flex items-center justify-between bg-container-brand-minimal rounded-lg px-4 h-12 py-0 border border-primary-500">
                  <div className="flex-1 mr-2">
                    <div className="flex items-center gap-1">
                      <span className="label-s-medium text-fg-brand-subtle">
                        {i18n.get("location")}
                      </span>
                      <span className="text-fg-error-subtle">*</span>
                    </div>
                    {isLoadingAddress ? (
                      <Loader2
                        className="animate-spin text-fg-brand-subtle mt-1"
                        size={16}
                      />
                    ) : (
                      <p className="label-m-medium text-fg-neutral-strong line-clamp-2">
                        {selectedAddress ||
                          i18n.get("noLocationSelected") ||
                          "No Location Selected"}
                      </p>
                    )}
                  </div>
                  {selectedAddress && (
                    <button
                      type="button"
                      onClick={() => {
                        AnalyticsService.trackGAEventHandler("changeLocationClearClicked");
                        AnalyticsService.trackMx("changeLocationClearClicked", {});
                        handleClearLocation();
                      }}
                      className="p-1 hover:bg-bg-brand-ghost-hover rounded-full transition-colors"
                      aria-label={i18n.get("clearLocation")}
                    >
                      <XIcon size={20} className="text-fg-brand-subtle" />
                    </button>
                  )}
                </div>

                {/* Confirm Button */}
                <button
                  type="button"
                  onClick={handleConfirmLocation}
                  disabled={!selectedAddress || isLoadingAddress}
                  className={cn(
                    "w-full mt-4 py-3 rounded-lg body-l-semibold transition-colors",
                    selectedAddress && !isLoadingAddress
                      ? "bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active text-fg-neutral-onFilled focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none"
                      : "bg-bg-brand-primary-disabled text-fg-neutral-minimal border border-border-token-neutral-minimal",
                  )}
                >
                  {i18n.get("selectLocation")}
                </button>
              </div>
            </div>
          ) : (
            // Default Search View
            <div className="flex-1 overflow-y-auto p-4 max-h-[calc(100vh-200px)]">
              {/* Search Input */}
              <div className="mb-4">
                <label
                  htmlFor="location-search"
                  className="block body-l-semibold text-fg-neutral-strong mb-2"
                >
                  {i18n.get("searchAreaOrCity")}
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    id="location-search"
                    type="text"
                    role="combobox"
                    aria-expanded={showSuggestionsView}
                    aria-controls={showSuggestionsView ? "location-suggestions-listbox" : undefined}
                    aria-activedescendant={
                      highlightIndex >= 0 && showSuggestionsView
                        ? `location-suggest-item-${highlightIndex}`
                        : undefined
                    }
                    aria-autocomplete="list"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setHighlightIndex(0); // Reset to first item when typing
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={i18n.get("searchAreaOrCity")}
                    className="w-full h-12 px-4 py-3 pr-12 text-base md:text-[13px] rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default text-fg-neutral-strong placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  />
                  {/* Clear Button - Only show when search text exists */}
                  {searchText && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchText("");
                        setHighlightIndex(-1);
                      }}
                      aria-label={i18n.get("clearSearch") || "Clear search"}
                      className={cn(
                        "absolute right-3 top-1/2 -translate-y-1/2",
                        "flex items-center justify-center flex-shrink-0",
                        "w-6 h-6 cursor-pointer",
                        "focus:outline-none focus:ring-2 focus:ring-primary-500 rounded",
                      )}
                    >
                      <XCircleIcon
                        size={24}
                        className="text-fg-neutral-strong"
                        strokeWidth={1.5}
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* Current Location and Map Options - Hidden when showing suggestions */}
              {!showSuggestionsView && (
                <>
                  {/* Current Location Option */}
                  <button
                    ref={(el) => {
                      if (el) defaultButtonRefs.current[0] = el;
                    }}
                    type="button"
                    onClick={handleCurrentLocationPress}
                    onKeyDown={(e) => handleDefaultButtonKeyDown(e, 0)}
                    className="w-full py-3 min-h-[48px] flex items-center gap-3 hover:bg-bg-brand-secondary-hover transition-colors rounded-lg px-2 -mx-2 focus-blue"
                    aria-label={i18n.get("currentLocation")}
                  >
                    <CrosshairIcon
                      size={24}
                      className="text-fg-neutral-strong flex-shrink-0"
                    />
                    <span className="label-m-medium text-fg-neutral-strong">
                      {i18n.get("currentLocation")}
                    </span>
                  </button>

                  {/* Divider */}
                  <div className="h-px bg-container-brand-minimal my-2" aria-hidden="true" />

                  {/* Set Location on Map Option */}
                  <button
                    ref={(el) => {
                      if (el) defaultButtonRefs.current[1] = el;
                    }}
                    type="button"
                    onClick={handleSetLocationOnMapPress}
                    onKeyDown={(e) => handleDefaultButtonKeyDown(e, 1)}
                    className="w-full py-3 min-h-[48px] flex items-center gap-3 hover:bg-bg-brand-secondary-hover transition-colors rounded-lg px-2 -mx-2 focus-blue"
                    aria-label={i18n.get("setLocationOnMap")}
                  >
                    <img
                      src={
                        isDark
                          ? "images/map-pin-simple-area_dark.svg"
                          : "images/map-pin-simple-area.svg"
                      }
                      alt=""
                      className="w-6 h-6"
                      aria-hidden="true"
                    />
                    <span className="label-m-medium text-fg-neutral-strong">
                      {i18n.get("setLocationOnMap")}
                    </span>
                  </button>

                  {/* Divider */}
                  <div className="h-px bg-container-brand-minimal my-2" aria-hidden="true" />
                </>
              )}

              {showSuggestionsView ? (
                // If user is searching, show suggestions list
                <div>{renderSuggestions()}</div>
              ) : (
                // Otherwise, show the popular cities
                <PopularCities
                  latitude={latitude}
                  longitude={longitude}
                  onCityPress={handleCityPress}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </Modal>
  );
}
